import { useId } from "react";
import { cx } from "@/lib/cx";

/**
 * 海図モチーフの背景（緯線・経線グリッド、等深線、ソナーの同心円、座標目盛り）。
 * ログイン画面とポータルのヒーロー帯で使う、本システムの「署名」となる装飾。
 */
export function ChartBackdrop({
  className,
  density = "normal",
  labels = true,
}: {
  className?: string;
  density?: "normal" | "sparse";
  /** 座標目盛りを表示 */
  labels?: boolean;
}) {
  const id = useId().replace(/:/g, "");
  const cell = density === "sparse" ? 96 : 64;
  return (
    <svg
      className={cx("bs-chart", className)}
      aria-hidden
      preserveAspectRatio="xMidYMid slice"
      viewBox="0 0 1200 800"
    >
      <defs>
        <pattern
          id={`g${id}`}
          width={cell}
          height={cell}
          patternUnits="userSpaceOnUse"
        >
          <path
            d={`M ${cell} 0 L 0 0 0 ${cell}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="0.6"
            opacity="0.22"
          />
        </pattern>
        <pattern
          id={`f${id}`}
          width={cell / 4}
          height={cell / 4}
          patternUnits="userSpaceOnUse"
        >
          <path
            d={`M ${cell / 4} 0 L 0 0 0 ${cell / 4}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="0.3"
            opacity="0.08"
          />
        </pattern>
        <radialGradient id={`v${id}`} cx="70%" cy="40%" r="75%">
          <stop offset="0%" stopColor="#2a6f8f" stopOpacity="0.55" />
          <stop offset="60%" stopColor="#0b2235" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="1200" height="800" fill={`url(#v${id})`} />
      <rect width="1200" height="800" fill={`url(#f${id})`} />
      <rect width="1200" height="800" fill={`url(#g${id})`} />

      {/* 等深線（島影の抽象） */}
      <g fill="none" stroke="currentColor" strokeWidth="0.9" opacity="0.3">
        <path d="M790 250c40-30 110-34 150-6 44 30 38 86-6 112-48 28-120 24-158-8-34-28-28-72 14-98z" />
        <path
          d="M762 238c56-48 156-54 210-12 60 46 52 128-12 166-66 40-170 34-222-14-48-44-40-102 24-140z"
          opacity="0.7"
        />
        <path
          d="M730 222c72-66 204-72 272-16 76 62 64 172-18 222-86 52-222 44-290-20-62-58-50-136 36-186z"
          opacity="0.45"
        />
        <path d="M300 560c26-18 70-20 94 0 26 22 18 56-12 70-32 14-76 8-96-14-16-20-10-42 14-56z" />
        <path
          d="M276 548c40-32 112-34 152-2 44 34 32 92-20 116-54 24-128 14-160-22-28-32-18-64 28-92z"
          opacity="0.6"
        />
        <path d="M1040 620c18-12 46-12 60 2 14 16 6 36-14 44-22 8-50 2-60-12-8-12-2-26 14-34z" />
      </g>

      {/* ソナーの同心円 */}
      <g fill="none" stroke="currentColor" opacity="0.18">
        <circle cx="866" cy="300" r="180" strokeWidth="0.8" />
        <circle
          cx="866"
          cy="300"
          r="290"
          strokeWidth="0.6"
          strokeDasharray="2 6"
        />
        <circle cx="866" cy="300" r="410" strokeWidth="0.6" />
      </g>
      <g className="bs-chart__sweep" style={{ transformOrigin: "866px 300px" }}>
        <path
          d="M866 300 L866 -110 A410 410 0 0 1 1156 10 Z"
          fill="currentColor"
          opacity="0.05"
        />
        <line
          x1="866"
          y1="300"
          x2="866"
          y2="-110"
          stroke="currentColor"
          strokeWidth="1"
          opacity="0.25"
        />
      </g>

      {/* 目標マーカー */}
      <g className="bs-chart__blips">
        <circle cx="930" cy="214" r="3.5" />
        <circle cx="760" cy="380" r="3" />
        <circle cx="1010" cy="340" r="2.5" />
      </g>

      {/* 座標目盛り */}
      {labels && (
        <g
          fontFamily="'IBM Plex Mono', monospace"
          fontSize="11"
          fill="currentColor"
          opacity="0.45"
          letterSpacing="1"
        >
          <text x="12" y={cell * 2 - 6}>
            27°N
          </text>
          <text x="12" y={cell * 5 - 6}>
            25°N
          </text>
          <text x="12" y={cell * 8 - 6}>
            23°N
          </text>
          <text x={cell * 3 + 6} y="788">
            123°E
          </text>
          <text x={cell * 8 + 6} y="788">
            125°E
          </text>
          <text x={cell * 13 + 6} y="788">
            127°E
          </text>
        </g>
      )}
    </svg>
  );
}
